import type { MouseEventHandler, TouchEventHandler } from "react";
import { useState, useRef, useEffect, useCallback } from "react";

interface BarControlProps {
  onClick?(value: number): void;
  onDragging?(value: number): void;
  onDragEnd?(value: number): void;
  onDragStart?(value: number): void;
}

/**
 * @returns 0 ~ 1 사이의 값을 반환합니다.
 */
function getProgress({ clientX }: { clientX: number }, boundaryRect?: DOMRect) {
  const clickedPosition = clientX;
  const offset = boundaryRect?.left ?? 0;
  const maxValue = boundaryRect?.width ?? 1;
  const currentValue = clickedPosition - offset;

  // 실제 너비를 넘어갈 수는 없음
  if (currentValue >= maxValue) {
    return 1;
  }

  // 실제 너비보다 작아질 수는 없음
  if (currentValue <= 0) {
    return 0;
  }

  return currentValue / maxValue;
}

export function useBarControl<BoundaryElement extends HTMLElement>({
  onDragging,
  onDragEnd,
  onDragStart,
}: BarControlProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<number>(0);
  const boundaryRef = useRef<BoundaryElement>(null);

  /**
   * 바에서 동글뱅이 눌러서 좌우로 이동 중
   * 실제 위치를 변경하지는 않지만 프로그레스 바는 마우스 따라서 이동해야 함
   */
  const handleWindowMouseMove = useCallback(
    (e: MouseEvent) => {
      const value = getProgress(
        { clientX: e.clientX },
        boundaryRef.current?.getBoundingClientRect(),
      );

      setDragPosition(value);

      if (typeof onDragging !== "undefined") {
        onDragging(value);
      }
    },
    [onDragging],
  );

  /**
   * 바에서 동글뱅이에서 마우스 뗌
   * 마우스 뗀 위치로 재생 위치 변경
   */
  const handleWindowMouseUp = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      const progress = getProgress(
        { clientX: e.clientX },
        boundaryRef.current?.getBoundingClientRect(),
      );

      setDragPosition(progress);

      window.removeEventListener("mousemove", handleWindowMouseMove, true);
      window.removeEventListener("mouseup", handleWindowMouseUp, true);

      setIsDragging(false);

      if (typeof onDragEnd !== "undefined") {
        onDragEnd(progress);
      }
    },
    [onDragEnd],
  );

  /**
   * 바에서 동글뱅이에 마우스 누름
   * 실제 재생 위치를 옮기지는 않고 프로그레스 바를 이동시킴
   */
  const handleButtonMouseDown: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    e.preventDefault();

    const progress = getProgress(
      { clientX: e.clientX },
      boundaryRef.current?.getBoundingClientRect(),
    );

    setDragPosition(progress);

    window.addEventListener("mousemove", handleWindowMouseMove, true);
    window.addEventListener("mouseup", handleWindowMouseUp, true);

    setIsDragging(true);

    if (typeof onDragStart !== "undefined") {
      onDragStart(progress);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener("mousemove", handleWindowMouseMove, true);
      window.removeEventListener("mouseup", handleWindowMouseUp, true);
    },
    [],
  );

  // 모바일 기기 용 터치 이벤트
  const handleWindowTouchMove = useCallback(
    (e: TouchEvent) => {
      const value = getProgress(
        { clientX: e.targetTouches[0].clientX },
        boundaryRef.current?.getBoundingClientRect(),
      );

      setDragPosition(value);

      if (typeof onDragging !== "undefined") {
        onDragging(value);
      }
    },
    [onDragging],
  );

  const handleWindowTouchEnd = useCallback(
    (e: TouchEvent) => {
      e.stopPropagation();

      const progress = getProgress(
        { clientX: e.changedTouches[0].clientX },
        boundaryRef.current?.getBoundingClientRect(),
      );

      setDragPosition(progress);

      window.removeEventListener("touchmove", handleWindowTouchMove, true);
      window.removeEventListener("touchend", handleWindowTouchEnd, true);

      setIsDragging(false);

      if (typeof onDragEnd !== "undefined") {
        onDragEnd(progress);
      }
    },
    [onDragEnd],
  );

  const handleButtonTouchStart: TouchEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();

    const progress = getProgress(
      { clientX: e.targetTouches[0].clientX },
      boundaryRef.current?.getBoundingClientRect(),
    );

    setDragPosition(progress);

    window.addEventListener("touchmove", handleWindowTouchMove, true);
    window.addEventListener("touchend", handleWindowTouchEnd, true);

    setIsDragging(true);

    if (typeof onDragStart !== "undefined") {
      onDragStart(progress);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener("touchmove", handleWindowTouchMove, true);
      window.removeEventListener("touchend", handleWindowTouchEnd, true);
    },
    [],
  );

  return {
    boundaryRef,
    dragPosition,
    isDragging,
    handleButtonMouseDown,
    handleButtonTouchStart,
  };
}
